// AuthContext.js の更新内容
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail,
  onAuthStateChanged ,
  setPersistence, browserLocalPersistence 
} from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, set, onValue, update } from 'firebase/database';


// Firebaseの設定（環境変数から取得するか、直接記述）
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};
// デバッグのために、環境変数がロードされているかを確認するコードを追加できます
// console.log("Firebase設定:", {
//   apiKey: process.env.REACT_APP_FIREBASE_API_KEY ? "存在します" : "存在しません",
//   authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ? "存在します" : "存在しません",
//   projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID ? "存在します" : "存在しません",
//   storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ? "存在します" : "存在しません",
//   messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID ? "存在します" : "存在しません",
//   appId: process.env.REACT_APP_FIREBASE_APP_ID ? "存在します" : "存在しません"
// });


// Firebaseの初期化
// const app = initializeApp(firebaseConfig);
// const auth = getAuth(app);

// AuthContext.js の認証初期化部分で追加

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
// 認証の永続性を設定
setPersistence(auth, browserLocalPersistence)
  .then(() => {
    console.log("Done");
  })
  .catch((error) => {
    console.error("認証永続性の設定エラー:", error);
  });

  
const database = getDatabase(app);

// 認証コンテキストの作成
const AuthContext = createContext();

// 認証プロバイダーコンポーネント
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ユーザー新規登録 - Realtime Databaseにユーザー情報も保存
  const signup = async (email, password) => {
    try {
      setError('');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Realtime Databaseにユーザーのプロフィールデータを保存
      await set(ref(database, 'users/' + userCredential.user.uid), {
        email: email,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
        paymentStatus: 'unpaid' // 支払い状態の初期値を追加
      });
      
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  // ログイン - 最終ログイン日時を更新
  const login = async (email, password) => {
    try {
      setError('');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // 最終ログイン日時を更新
      const updates = {};
      updates[`users/${userCredential.user.uid}/lastLogin`] = new Date().toISOString();
      await update(ref(database), updates);
      
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  // ログアウト
  const logout = async () => {
    try {
      setError('');
      await signOut(auth);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  // パスワードリセット
  const resetPassword = async (email) => {
    try {
      setError('');
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  // 認証状態の監視
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // ユーザープロフィールを取得する関数
  const getUserProfile = async (userId) => {
    try {
      return new Promise((resolve, reject) => {
        const userRef = ref(database, `users/${userId}`);
        onValue(userRef, (snapshot) => {
          const data = snapshot.val();
          resolve(data);
        }, {
          onlyOnce: true
        }, (error) => {
          reject(error);
        });
      });
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  // ユーザープロフィールを更新する関数
  const updateUserProfile = async (userId, profileData) => {
    try {
      setError('');
      const updates = {};
      updates[`users/${userId}`] = { ...profileData, updatedAt: new Date().toISOString() };
      await update(ref(database), updates);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  // 支払い状態を更新する関数
  const updatePaymentStatus = async (userId, status, timestamp = new Date().toISOString()) => {
    try {
      setError('');
      const updates = {};
      
      if (status === 'pending') {
        updates[`users/${userId}/paymentStatus`] = status;
        updates[`users/${userId}/paymentRequestedAt`] = timestamp;
      } else if (status === 'completed') {
        updates[`users/${userId}/paymentStatus`] = status;
        updates[`users/${userId}/paymentCompletedAt`] = timestamp;
      } else {
        updates[`users/${userId}/paymentStatus`] = 'unpaid';
      }
      
      await update(ref(database), updates);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  // 管理者アカウントを確認する関数
  const isAdmin = async (userId) => {
    try {
      const userRef = ref(database, `users/${userId}`);
      return new Promise((resolve, reject) => {
        onValue(userRef, (snapshot) => {
          const userData = snapshot.val();
          resolve(userData?.isAdmin === true);
        }, {
          onlyOnce: true
        }, (error) => {
          reject(error);
        });
      });
    } catch (err) {
      console.error("管理者権限チェックエラー:", err);
      return false;
    }
  };

  const value = {
    currentUser,
    signup,
    login,
    logout,
    resetPassword,
    getUserProfile,
    updateUserProfile,
    updatePaymentStatus,
    isAdmin, 
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// 認証コンテキストを使用するためのカスタムフック
export const useAuth = () => {
  return useContext(AuthContext);
};